import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, MessageCircle, Phone } from 'lucide-react';
import { Button } from './ui/button';

const faqs = [
  {
    category: 'Services & Scope',
    items: [
      {
        question: "What all finance & accounting functions do you handle?",
        answer: "We handle your entire finance department: daily bookkeeping, bank reconciliation, accounts payable/receivable, GST filing, TDS compliance & returns, income tax filings, annual MCA/ROC filings, payroll processing, PF/ESI compliance, financial statements, MIS reports, cash flow forecasting, budget planning, and audit support. From Starter itself, you get a complete outsourced finance & accounts function."
      },
      {
        question: "How is this different from hiring an in-house accountant?",
        answer: "Unlike a single in-house accountant, you get access to an entire team of CAs, tax experts, and compliance specialists at a fraction of the cost. We bring diverse expertise, use the latest tools, and provide dedicated support - something impossible with a single hire. Plus, no HR headaches, leave management, or training costs."
      },
      {
        question: "Do you handle TDS compliance and income tax filings?",
        answer: "Yes! TDS compliance and income tax filings are included from our Starter plan itself. This covers TDS return filing, TDS payment tracking, Form 16/16A generation, advance tax calculations, and complete income tax return filing for your business."
      },
      {
        question: "What about annual MCA/ROC filings?",
        answer: "Annual MCA filings (ROC compliance) are included in every plan. This covers annual returns (Form AOC-4, Form MGT-7), director KYC, board resolution filings, and all other mandatory MCA compliance for private limited and LLP companies."
      }
    ]
  },
  {
    category: 'Getting Started',
    items: [
      {
        question: "How quickly can you start working on my accounts?",
        answer: "We can onboard you within 48-72 hours. After our initial consultation, we'll assess your current financial situation, set up necessary integrations with your banking and accounting software, and begin work immediately. Most clients see their first MIS report within the first week."
      },
      {
        question: "What software/tools do you use?",
        answer: "We're technology-agnostic and can work with your existing tools - Tally, Zoho Books, QuickBooks, etc. We also provide access to our cloud-based dashboard for real-time financial visibility. All integrations are handled by our team at no extra cost."
      },
      {
        question: "What if I'm not satisfied with the service?",
        answer: "We offer a 30-day money-back guarantee. If you're not completely satisfied within the first month, we'll refund your payment in full - no questions asked. We're confident in our service quality, but your peace of mind comes first."
      }
    ]
  },
  {
    category: 'Security & Compliance',
    items: [
      {
        question: "Is my financial data secure with Virtual CFO Consultancy?",
        answer: "Absolutely. We follow bank-grade encryption, secure cloud storage, and strict access controls. All our team members sign NDAs, and we follow ICAI's ethical guidelines. Your data is backed up daily and never shared with third parties."
      },
      {
        question: "Can you help with investor due diligence and fundraising?",
        answer: "Yes! Many of our clients have successfully raised funding after working with us. We ensure your books are investor-ready, prepare data rooms, create financial projections, and can directly coordinate with investors' due diligence teams. We've supported clients through Series A, B, and beyond."
      },
      {
        question: "Do you work with businesses outside India?",
        answer: "We primarily serve Indian businesses and Indian subsidiaries of foreign companies. We handle all Indian compliance requirements including GST, TDS, ROC filings, and FEMA regulations for companies with foreign investment."
      }
    ]
  }
];

export const FAQSection = ({ onBookAppointment }) => {
  const [openIndex, setOpenIndex] = useState('0-0');

  const toggleFaq = (key) => {
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <section id="faqs" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <HelpCircle className="w-4 h-4" />
            Frequently Asked Questions
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Got Questions? We've Got Answers
          </h2>
          <p className="text-lg text-gray-600">
            Everything you need to know about outsourcing your finance & accounts
          </p>
        </div>

        <div className="space-y-8 mb-12">
          {faqs.map((section, sIdx) => (
            <div key={sIdx}>
              <h3 className="text-sm font-bold text-emerald-700 uppercase tracking-wider mb-3 px-2">
                {section.category}
              </h3>
              <div className="space-y-3">
                {section.items.map((faq, fIdx) => {
                  const key = `${sIdx}-${fIdx}`;
                  const isOpen = openIndex === key;
                  return (
                    <div
                      key={key}
                      data-testid={`faq-item-${sIdx}-${fIdx}`}
                      className={`bg-white rounded-xl border ${isOpen ? 'border-emerald-300 shadow-lg' : 'border-gray-200'} overflow-hidden transition-all duration-300`}
                    >
                      <button
                        onClick={() => toggleFaq(key)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className={`font-semibold pr-4 ${isOpen ? 'text-emerald-700' : 'text-gray-900'}`}>
                          {faq.question}
                        </span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-5">
                          <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-3">Still Have Questions?</h3>
          <p className="text-emerald-100 mb-6">
            Our experts are here to help. Get personalized answers in a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onBookAppointment}
              data-testid="faq-book-consultation-btn"
              size="lg"
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-8 py-6 text-lg rounded-full font-semibold"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Book Free Consultation
            </Button>
            <a
              href="https://wa.me/917977789741"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 px-8 py-4 rounded-full font-semibold transition-colors"
            >
              <Phone className="w-5 h-5" />
              WhatsApp: +91 7977789741
            </a>
          </div>
          <p className="text-emerald-200 text-sm mt-4">
            Available Monday - Saturday, 11 AM - 8 PM IST
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
